import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Input, Button, Checkbox, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useC5M2, ItemState } from './hooks/useC5M2';
import { c5m2Api } from '../../services/c5m2Api';
import { BatchAcceptanceBridge, pwc5m2f1AcceptanceManifest } from '../../features/acceptance';
import './C5M2Form.css';

// -- Helpers --
const fmtNumber = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

const fmtPercent = (v: number | null | undefined): string => {
  if (v == null) return '';
  return v.toFixed(2) + '%';
};

// -- Component --
const C5M2Form: React.FC = () => {
  const navigate = useNavigate();
  const [startNo, setStartNo] = useState('');
  const [endNo, setEndNo] = useState('');

  const {
    items,
    loading,
    executeLoading,
    queried,
    selectedCount,
    handleQuery,
    handleToggleItem,
    handleSelectAll,
    handleExecute,
    lastApiResponse,
  } = useC5M2();

  // Auto-load all eligible items on mount
  useEffect(() => { handleQuery('', ''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -- Query --
  const onQuery = useCallback(() => {
    handleQuery(startNo, endNo);
  }, [handleQuery, startNo, endNo]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') onQuery();
    },
    [onQuery]
  );

  // -- Execute --
  const onExecute = useCallback(() => {
    Modal.confirm({
      title: '確認沖銷',
      content: `確定要沖銷已勾選的 ${selectedCount} 筆S/C？此操作不可復原。`,
      okText: '確定沖銷',
      okType: 'primary',
      cancelText: '取消',
      onOk: async () => {
        await handleExecute();
      },
    });
  }, [selectedCount, handleExecute]);

  // -- Select All --
  const allSelected = items.length > 0 && items.every((s) => s.selected);
  const someSelected = items.some((s) => s.selected) && !allSelected;

  // -- Table columns --
  const columns: ColumnsType<ItemState> = [
    {
      title: (
        <Checkbox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      dataIndex: 'selected',
      key: 'selected',
      width: 50,
      align: 'center',
      render: (_: unknown, record: ItemState) => (
        <Checkbox
          checked={record.selected}
          onChange={(e) =>
            handleToggleItem(record.item.fc110_01, e.target.checked)
          }
        />
      ),
    },
    {
      title: <span data-contract-field="fc110_01">S/C 單號</span>,
      dataIndex: ['item', 'fc110_01'],
      key: 'fc110_01',
      width: 160,
    },
    {
      title: <span data-contract-field="fc110_12">客戶名稱</span>,
      dataIndex: ['item', 'fc110_12'],
      key: 'fc110_12',
      width: 200,
      ellipsis: true,
    },
    {
      title: <span data-contract-field="fc110_99">毛利金額</span>,
      dataIndex: ['item', 'fc110_99'],
      key: 'fc110_99',
      width: 130,
      align: 'right',
      render: (v: number) => fmtNumber(v),
    },
    {
      title: <span data-contract-field="fc110_100">毛利率</span>,
      dataIndex: ['item', 'fc110_100'],
      key: 'fc110_100',
      width: 100,
      align: 'right',
      render: (v: number) => fmtPercent(v),
    },
    {
      title: <span data-contract-field="fc110_90">版本</span>,
      dataIndex: ['item', 'fc110_90'],
      key: 'fc110_90',
      width: 70,
      align: 'center',
    },
  ];

  // -- Render --
  return (
    <div className="c5m2-form">
      {(loading || executeLoading) && (
        <div className="loading-overlay">
          {executeLoading ? '沖銷執行中...' : '載入中...'}
        </div>
      )}

      <div className="page-wrapper">
        {/* Page Title Bar */}
        <div className="page-title-bar">
          <div className="page-title-left">
            <nav className="breadcrumb-inline">
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                首頁
              </a>
              <span>/</span>
              <a
                href="#/erp"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/erp');
                }}
              >
                銷貨管理
              </a>
              <span>/</span>
              <span className="current">S/C沖銷核准</span>
            </nav>
            <span className="title-separator">&mdash;</span>
            <h1>S/C沖銷核准</h1>
            <span className="badge">C5M2</span>
          </div>
        </div>

        {/* Query Bar */}
        <div className="query-bar">
          <label>S/C 單號 .從:</label>
          <Input
            value={startNo}
            onChange={(e) => setStartNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="起始單號"
            style={{ width: 180 }}
            allowClear
          />
          <span className="separator">至</span>
          <Input
            value={endNo}
            onChange={(e) => setEndNo(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="結束單號"
            style={{ width: 180 }}
            allowClear
          />
          <Button
            type="primary"
            onClick={onQuery}
            loading={loading}
            data-acceptance-action="PWC5M2-ACT-QUERY"
          >
            確定範圍
          </Button>
        </div>

        {/* Table Card */}
        <div className="table-card">
          {queried && (
            <div className="table-header">
              <div className="info">
                共 <strong>{items.length}</strong> 筆
                {selectedCount > 0 && (
                  <span>
                    ，已勾選 <strong>{selectedCount}</strong> 筆
                  </span>
                )}
              </div>
            </div>
          )}

          {queried ? (
            <Table<ItemState>
              columns={columns}
              dataSource={items}
              rowKey={(record) => record.item.fc110_01}
              pagination={false}
              size="small"
              scroll={{ y: 400 }}
              locale={{ emptyText: '查無資料' }}
            />
          ) : (
            <div className="empty-state">
              請輸入S/C單號範圍並點擊「確定範圍」查詢
            </div>
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="action-bar">
          <Button
            type="primary"
            size="large"
            disabled={selectedCount === 0 || executeLoading}
            loading={executeLoading}
            onClick={onExecute}
            data-acceptance-action="PWC5M2-ACT-EXECUTE"
          >
            存檔 ({selectedCount})
          </Button>
          <Button
            danger
            size="large"
            onClick={() => navigate('/erp')}
          >
            離開
          </Button>
        </div>
        <BatchAcceptanceBridge
          manifest={pwc5m2f1AcceptanceManifest}
          records={items.map(state => state.item)}
          selectedKeys={items.filter(state => state.selected).map(state => state.item.fc110_01)}
          keyOf={(item) => item.fc110_01}
          queryExact={async (key) => {
            const result = await c5m2Api.query(key, key);
            return result.items[0] || null;
          }}
          lastApiResponse={lastApiResponse}
          loading={loading || executeLoading}
          onAutoFill={() => {
            const first = items[0];
            if (first) handleToggleItem(first.item.fc110_01, true);
          }}
          autoFillDisabled={items.length === 0}
          autoFillReason="安全勾選第一張可沖銷 S/C，不會自動執行沖銷。"
          screenDraft={{ start_no: startNo, end_no: endNo, selected_count: selectedCount }}
        />
      </div>
    </div>
  );
};

export default C5M2Form;
